import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import isNil from 'lodash/isNil'
import type { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'
import {
  formatPaymentTypeLabel,
  TEMPLATE_ROWS,
  UPLOAD_COLUMN_WIDTHS,
  UPLOAD_HEADERS,
  type ParsedRow,
} from './bulkUpload.helpers'

const SHEET_NAME = 'Billing Profiles'

interface UploadSheetRow extends Record<string, string> {
  'Client Name': string
  'To Emails': string
  'Payment Type': string
  'Due Day': string
}

const writeWorkbook = (
  rows: UploadSheetRow[] | typeof TEMPLATE_ROWS,
  fileName: string,
  header?: string[]
) => {
  const sheet = XLSX.utils.json_to_sheet(rows, header ? { header } : undefined)
  sheet['!cols'] = UPLOAD_COLUMN_WIDTHS
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, SHEET_NAME)
  XLSX.writeFile(workbook, fileName)
}

export const downloadTemplate = () =>
  writeWorkbook(TEMPLATE_ROWS, 'billing-profile-template.xlsx')

const ACCOUNTS_SHEET_NAME = 'Accounts'
const ACCOUNTS_HEADERS = ['Email', 'Client Name'] as const
const ACCOUNTS_COLUMN_WIDTHS = [{ wch: 34 }, { wch: 25 }]

/** What the billing-profile export can write. The roster is its own scope. */
export type ProfileExportScope = 'COMPANIES' | 'ACCOUNTS' | 'BOTH'

/** Every scope the shared Export modal can offer, across both tabs. */
export type ExportScope = ProfileExportScope | 'FUNDING_MANAGERS'

export type ExportFormat = 'XLSX' | 'CSV'

/** One choice in the Export modal's "What to include". */
export interface ExportScopeOption<TScope extends ExportScope = ExportScope> {
  value: TScope
  label: string
  /** What the file holds, and whether the page's filters narrow it. */
  hint: string
}

export const PROFILE_EXPORT_SCOPES: ExportScopeOption<ProfileExportScope>[] = [
  {
    value: 'COMPANIES',
    label: 'Companies',
    hint: 'One row per company, matching the filters on this page.',
  },
  {
    value: 'ACCOUNTS',
    label: 'Invoice recipients',
    hint: 'One row per invoice recipient, CC addresses excluded.',
  },
  {
    value: 'BOTH',
    label: 'Both',
    hint: 'Companies and invoice recipients, on a sheet each.',
  },
]

/** One row per company, matching the upload template so an export can be re-uploaded. */
export const buildCompanyRows = (
  profiles: BillingProfileRecord[]
): UploadSheetRow[] =>
  profiles.map((profile) => ({
    'Client Name': profile.clientName,
    'To Emails': profile.toEmails.join('; '),
    'Payment Type': formatPaymentTypeLabel(profile.paymentType),
    'Due Day': isNil(profile.dueDay) ? '' : String(profile.dueDay),
  }))

/**
 * One row per delivery address. Only `toEmails` is listed, so these counts can
 * sit below the User view roster, which also unions the CC addresses.
 */
export const buildAccountRows = (
  profiles: BillingProfileRecord[]
): Record<string, string>[] =>
  profiles.flatMap((profile) =>
    profile.toEmails.map((email) => ({
      Email: email,
      'Client Name': profile.clientName,
    }))
  )

export interface ExportSheet {
  name: string
  rows: Record<string, string>[]
  header: string[]
  widths: { wch: number }[]
}

const buildSheets = (
  profiles: BillingProfileRecord[],
  scope: ProfileExportScope
): ExportSheet[] => {
  const companies: ExportSheet = {
    name: SHEET_NAME,
    rows: buildCompanyRows(profiles),
    header: [...UPLOAD_HEADERS],
    widths: UPLOAD_COLUMN_WIDTHS,
  }
  const accounts: ExportSheet = {
    name: ACCOUNTS_SHEET_NAME,
    rows: buildAccountRows(profiles),
    header: [...ACCOUNTS_HEADERS],
    widths: ACCOUNTS_COLUMN_WIDTHS,
  }
  if (scope === 'COMPANIES') return [companies]
  if (scope === 'ACCOUNTS') return [accounts]
  return [companies, accounts]
}

const FILE_STEM: Record<ProfileExportScope, string> = {
  COMPANIES: 'billing-profiles-current',
  ACCOUNTS: 'billing-profiles-accounts',
  BOTH: 'billing-profiles-full',
}

/**
 * Writes one file from the given sheets. CSV holds a single table, so callers
 * only offer it for a single sheet.
 */
export const downloadExportWorkbook = (
  sheets: ExportSheet[],
  fileStem: string,
  format: ExportFormat
) => {
  const workbook = XLSX.utils.book_new()

  for (const { name, rows, header, widths } of sheets) {
    const sheet = XLSX.utils.json_to_sheet(rows, { header })
    sheet['!cols'] = widths
    XLSX.utils.book_append_sheet(workbook, sheet, name)
  }

  if (format === 'CSV') {
    XLSX.writeFile(workbook, `${fileStem}.csv`, { bookType: 'csv' })
    return
  }
  XLSX.writeFile(workbook, `${fileStem}.xlsx`)
}

export const downloadProfilesExport = (
  profiles: BillingProfileRecord[],
  scope: ProfileExportScope,
  format: ExportFormat
) =>
  downloadExportWorkbook(buildSheets(profiles, scope), FILE_STEM[scope], format)

/** Current values shaped like the upload template, so the file can be edited and re-uploaded. */
export const downloadProfilesWorkbook = (profiles: BillingProfileRecord[]) =>
  downloadProfilesExport(profiles, 'COMPANIES', 'XLSX')

/** Round-trips the edited preview rows back into the .xlsx the API expects. */
export const buildUploadFile = (rows: ParsedRow[]): File => {
  const sheet = XLSX.utils.json_to_sheet(
    rows.map((row) => ({
      'Client Name': row.clientName,
      'To Emails': row.toEmails,
      'Payment Type': row.paymentType,
      'Due Day': row.dueDay,
    }))
  )
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, SHEET_NAME)
  const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  return new File([output], 'upload.xlsx', {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

export class BulkUploadParseError extends Error {}

/**
 * Reads an Excel date cell as its day of the month, for anyone who types a
 * whole date into the Due Day column.
 *
 * SheetJS builds these Dates a few seconds short of local midnight, so reading
 * one directly lands on the previous calendar day. Rounding to the nearest
 * minute absorbs that without moving a real time of day.
 */
export const formatDueDayCell = (value: Date): string =>
  String(dayjs(value).add(30, 'second').startOf('minute').date())

/**
 * Reads the first sheet into preview rows. Headers are matched case- and
 * whitespace-insensitively, and Excel date cells are reduced to a day of the
 * month so the preview and the re-uploaded file agree.
 */
export const parseUploadWorkbook = (
  buffer: ArrayBuffer,
  isKnownClientName: (name: string) => boolean
): ParsedRow[] => {
  const workbook = XLSX.read(new Uint8Array(buffer), {
    type: 'array',
    cellDates: true,
  })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) {
    throw new BulkUploadParseError('Excel file contains no sheets')
  }

  const jsonRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
    workbook.Sheets[sheetName],
    { defval: '' }
  )
  if (jsonRows.length === 0) {
    throw new BulkUploadParseError('Excel file contains no data rows')
  }

  const rows: ParsedRow[] = jsonRows.map((row, index) => {
    const normalized: Record<string, string> = {}
    for (const [key, value] of Object.entries(row)) {
      normalized[key.trim().toLowerCase()] =
        value instanceof Date ? formatDueDayCell(value) : String(value).trim()
    }
    const clientName = normalized['client name'] ?? ''
    return {
      rowNum: index + 1,
      clientName,
      toEmails: normalized['to emails'] ?? '',
      paymentType: normalized['payment type'] ?? '',
      dueDay: normalized['due day'] ?? '',
      clientMatch: clientName === '' ? false : isKnownClientName(clientName),
    }
  })

  // A row carrying only a client name is a no-op under partial updates, so it
  // never needs to appear in the preview.
  const rowsWithUpdates = rows.filter(
    (row) =>
      row.toEmails.trim() !== '' ||
      row.paymentType.trim() !== '' ||
      row.dueDay.trim() !== ''
  )
  if (rowsWithUpdates.length === 0) {
    throw new BulkUploadParseError('No rows with values to update')
  }

  return rowsWithUpdates
}
